import React, { useState, useEffect } from 'react';
import { AlertTriangle, X, Calendar, FileText, DollarSign, Search, Users } from 'lucide-react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { 
  createBlacklistIncident, 
  updateIncident, 
  validateIncidentData 
} from '../lib/blacklistService';
import { getAllClients } from '../lib/clientService';
import { matchesSearchTerm } from '../lib/searchUtils';
import { useAuth } from '../contexts/AuthContext';

const BlacklistIncidentForm = ({ 
  isOpen, 
  onClose, 
  onSuccess, 
  clientId,
  clientName,
  preselectedOrderId = null,
  editingIncident = null 
}) => {
  const { currentUser } = useAuth();
  const [formData, setFormData] = useState({
    ordenFolio: '',
    fechaIncidente: new Date().toISOString().split('T')[0],
    motivoIncidente: '',
    detallesIncidente: '',
    montoDeuda: ''
  });
  const [availableOrders, setAvailableOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  
  // Client selection state (when no clientId is provided)
  const [selectedClient, setSelectedClient] = useState(null);
  const [allClients, setAllClients] = useState([]);
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [showClientSearch, setShowClientSearch] = useState(false);
  
  const isEditing = !!editingIncident;

  // Effect for form initialization when modal opens or editing changes
  useEffect(() => {
    if (isOpen) {
      if (isEditing) {
        // Populate form with existing incident data
        setFormData({
          ordenFolio: editingIncident.ordenFolio || '',
          fechaIncidente: editingIncident.fechaIncidente ? 
            (editingIncident.fechaIncidente.toDate ? 
              editingIncident.fechaIncidente.toDate().toISOString().split('T')[0] :
              new Date(editingIncident.fechaIncidente).toISOString().split('T')[0]
            ) : new Date().toISOString().split('T')[0],
          motivoIncidente: editingIncident.motivoIncidente || '',
          detallesIncidente: editingIncident.detallesIncidente || '',
          montoDeuda: editingIncident.montoDeuda ? editingIncident.montoDeuda.toString() : ''
        });
      } else {
        // Reset form for new incident
        setFormData({
          ordenFolio: preselectedOrderId || '',
          fechaIncidente: new Date().toISOString().split('T')[0],
          motivoIncidente: '',
          detallesIncidente: '',
          montoDeuda: ''
        });
        
        // Reset client selection for new incidents
        setSelectedClient(null);
        setClientSearchTerm('');
        setShowClientSearch(false);
      }
      
      // Load clients if no clientId is provided (new incident)
      if (!clientId && !isEditing) {
        loadClients();
      }
      
      setErrors([]);
    }
  }, [isOpen, clientId, preselectedOrderId, editingIncident, isEditing]);

  // Effect for fetching orders when client is selected
  useEffect(() => {
    if (isOpen) {
      const currentClientId = clientId || selectedClient?.id;
      console.log('Orders useEffect - currentClientId:', currentClientId, 'selectedClient:', selectedClient);
      if (currentClientId) {
        console.log('Fetching orders for client:', currentClientId);
        fetchClientOrders(currentClientId);
      }
    }
  }, [isOpen, clientId, selectedClient]);

  const loadClients = async () => {
    try {
      const clients = await getAllClients();
      // Filter out archived clients
      const activeClients = clients.filter(client => !client.isArchived);
      setAllClients(activeClients);
    } catch (error) {
      console.error('Error loading clients:', error);
      setAllClients([]);
    }
  };

  const fetchClientOrders = async (targetClientId = null) => {
    const currentClientId = targetClientId || clientId;
    console.log('fetchClientOrders called with:', targetClientId, 'currentClientId:', currentClientId);
    if (!currentClientId) return;
    
    try {
      const ordersQuery = query(
        collection(db, 'orders'),
        where('cliente', '==', currentClientId)
      );
      
      const ordersSnapshot = await getDocs(ordersQuery);
      const ordersList = ordersSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Sort manually by fechaCreacion descending to avoid composite index requirement
      ordersList.sort((a, b) => {
        const dateA = a.fechaCreacion?.toDate ? a.fechaCreacion.toDate() : new Date(a.fechaCreacion || 0);
        const dateB = b.fechaCreacion?.toDate ? b.fechaCreacion.toDate() : new Date(b.fechaCreacion || 0);
        return dateB - dateA;
      });

      console.log('Orders fetched:', ordersList);
      setAvailableOrders(ordersList);
    } catch (error) {
      console.error('Error fetching client orders:', error);
      setAvailableOrders([]);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear errors when user starts typing
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleClientSelect = (client) => {
    console.log('Client selected:', client);
    setSelectedClient(client);
    setShowClientSearch(false);
    setClientSearchTerm('');
    // Reset form order selection when client changes
    setFormData(prev => ({ ...prev, ordenFolio: '' }));
  };

  const getClientDisplayName = (client) => {
    const principalContact = client.contactos?.find(c => c.esPrincipal) || client.contactos?.[0];
    return client.nombreEmpresa || principalContact?.nombre || 'Cliente';
  };

  // Filter clients based on search term
  const filteredClients = allClients.filter(client => {
    if (!clientSearchTerm) return true;
    
    const searchableText = [
      client.nombreEmpresa,
      ...(client.contactos || []).map(c => c.nombre),
      ...(client.contactos || []).map(c => c.telefono),
    ].filter(Boolean).join(' ').toLowerCase();
    
    return matchesSearchTerm(searchableText, clientSearchTerm);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors([]);

    try {
      // Prepare incident data
      const currentClientId = clientId || selectedClient?.id;
      const incidentData = {
        clienteId: currentClientId,
        ordenFolio: formData.ordenFolio,
        fechaIncidente: new Date(formData.fechaIncidente),
        motivoIncidente: formData.motivoIncidente.trim(),
        detallesIncidente: formData.detallesIncidente.trim(),
        montoDeuda: formData.montoDeuda ? parseFloat(formData.montoDeuda) : null,
        usuarioCreador: currentUser?.uid || 'system'
      };

      // Validate data
      const validation = validateIncidentData(incidentData);
      if (!validation.isValid) {
        setErrors(validation.errors);
        setLoading(false);
        return;
      }

      if (isEditing) {
        // Update existing incident
        await updateIncident(editingIncident.id, incidentData);
      } else {
        // Create new incident
        await createBlacklistIncident(incidentData);
      }

      onSuccess(isEditing ? 'Incidente actualizado exitosamente' : 'Cliente agregado a lista negra exitosamente');
      onClose();
    } catch (error) {
      console.error('Error saving blacklist incident:', error);
      setErrors([error.message || 'Error al guardar el incidente']);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black dark:bg-gray-900 bg-opacity-50 dark:bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <AlertTriangle className="h-6 w-6 text-red-600 dark:text-red-400" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {isEditing ? 'Editar Incidente' : 'Agregar a Lista Negra'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-md"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {(clientName || selectedClient) && (
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Cliente: <span className="font-medium">{clientName || getClientDisplayName(selectedClient)}</span>
            </p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6 bg-white dark:bg-gray-800">
          {/* Error Messages */}
          {errors.length > 0 && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-600 dark:text-red-400 px-4 py-3 rounded-md">
              <ul className="list-disc list-inside space-y-1">
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          {/* Client Selection - Only show when no clientId is provided */}
          {(!clientId && !selectedClient) && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <Users className="inline h-4 w-4 mr-1" />
                Seleccionar Cliente *
              </label>
              
              {!showClientSearch ? (
                <button
                  type="button"
                  onClick={() => setShowClientSearch(true)}
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md text-left text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400"
                >
                  <Search className="inline h-4 w-4 mr-2" />
                  Buscar cliente...
                </button>
              ) : (
                <div className="space-y-2">
                  <div className="relative">
                    <input
                      type="text"
                      value={clientSearchTerm}
                      onChange={(e) => setClientSearchTerm(e.target.value)}
                      placeholder="Buscar por nombre, empresa o teléfono..."
                      className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      autoFocus
                    />
                    <button
                      type="button"
                      onClick={() => setShowClientSearch(false)}
                      className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  
                  {/* Client search results */}
                  <div className="max-h-60 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700">
                    {filteredClients.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 dark:text-gray-400">
                        {clientSearchTerm ? 'No se encontraron clientes' : 'Escribe para buscar clientes'}
                      </div>
                    ) : (
                      filteredClients.slice(0, 10).map(client => (
                        <button
                          key={client.id}
                          type="button"
                          onClick={() => handleClientSelect(client)}
                          className="w-full p-3 text-left hover:bg-gray-50 dark:hover:bg-gray-600 border-b border-gray-200 dark:border-gray-600 last:border-b-0"
                        >
                          <div className="font-medium text-gray-900 dark:text-white">
                            {getClientDisplayName(client)}
                          </div>
                          {client.nombreEmpresa && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              {client.nombreEmpresa}
                            </div>
                          )}
                          {client.contactos && client.contactos[0]?.telefono && (
                            <div className="text-sm text-gray-500 dark:text-gray-400">
                              📞 {client.contactos[0].telefono}
                            </div>
                          )}
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Order Selection - Only show when we have a client */}
          {(clientId || selectedClient) && (
            <div>
              <label htmlFor="ordenFolio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <FileText className="inline h-4 w-4 mr-1" />
                Orden Asociada *
              </label>
              {preselectedOrderId ? (
                <div className="p-3 bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md">
                  <span className="text-sm font-medium text-gray-900 dark:text-white">{formData.ordenFolio}</span>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Orden preseleccionada</p>
                </div>
              ) : (
                <select
                  id="ordenFolio"
                  name="ordenFolio"
                  value={formData.ordenFolio}
                  onChange={handleInputChange}
                  required
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Seleccionar orden...</option>
                  {availableOrders.map(order => (
                    <option key={order.id} value={order.folio}>
                      {order.folio} - {order.fecha ? new Date(order.fecha.toDate()).toLocaleDateString() : 'Sin fecha'}
                    </option>
                  ))}
                </select>
              )}
              
              {availableOrders.length === 0 && (clientId || selectedClient) && (
                <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                  No se encontraron órdenes para este cliente
                </p>
              )}
            </div>
          )}

          {/* Incident Details - Only show when we have a client */}
          {(clientId || selectedClient) && (
            <>
              <div>
                <label htmlFor="fechaIncidente" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  <Calendar className="inline h-4 w-4 mr-1" />
                  Fecha del Incidente *
                </label>
                <input
                  type="date"
                  id="fechaIncidente"
                  name="fechaIncidente"
                  value={formData.fechaIncidente}
                  onChange={handleInputChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                  className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
                />
              </div>

              <div>
              <label htmlFor="motivoIncidente" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Motivo del Incidente *
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(Resumen corto)</span>
              </label>
              <input
                type="text"
                id="motivoIncidente"
                name="motivoIncidente"
                value={formData.motivoIncidente}
                onChange={handleInputChange}
                placeholder="ej. No pagó servicio autorizado"
                maxLength={100}
                required
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {formData.motivoIncidente.length}/100 caracteres
              </p>
            </div>

            <div>
              <label htmlFor="detallesIncidente" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Detalles del Incidente *
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(Descripción detallada)</span>
              </label>
              <textarea
                id="detallesIncidente"
                name="detallesIncidente"
                value={formData.detallesIncidente}
                onChange={handleInputChange}
                placeholder="Describe detalladamente lo que ocurrió, las acciones tomadas, y cualquier información relevante..."
                rows={4}
                required
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
            </div>

            <div>
              <label htmlFor="montoDeuda" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                <DollarSign className="inline h-4 w-4 mr-1" />
                Monto de Deuda (Opcional)
              </label>
              <input
                type="number"
                id="montoDeuda"
                name="montoDeuda"
                value={formData.montoDeuda}
                onChange={handleInputChange}
                placeholder="0.00"
                min="0"
                step="0.01"
                className="w-full p-3 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
              />
              </div>
            </>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-6 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400 disabled:opacity-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-red-600 dark:bg-red-500 border border-transparent rounded-md hover:bg-red-700 dark:hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 disabled:opacity-50 flex items-center space-x-2"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              )}
              <span>{isEditing ? 'Actualizar Incidente' : 'Agregar a Lista Negra'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BlacklistIncidentForm;